// Settings (signed language): every page here is GENERATED from a configurator
// in @aiw/config. Left: the kinds. Right: a form from the selected kind's
// schema, pre-filled from its defaults. Save proposes set_config; the kind's
// scope chip says what that reaches ("a note" or "our records").
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { configKinds, ArtemisServices } from "@aiw/config";
import { Button, Input, KindChip, caseKind, cn } from "@aiw/ui";
import { Page } from "../shell/Page";
import { OUTCOME_TONE, useConfigMeta, useProposer } from "../hooks/slice-b";
import { day } from "../lib/format";
import { SettingsForm, type Schema } from "../components/settings-form";
import {StandardServiceSettings} from '../components/standard-service-settings';
import {useT} from '../lib/i18n';
import { AgentAccountCard } from "../components/agent-account-card";
import { MissingRates } from "../components/missing-rates";
import { authHeaders, fetchImpl } from "../lib/api";
import { env } from "../lib/env";

type Kind = (typeof configKinds)[keyof typeof configKinds];
interface Issue { path: PropertyKey[]; message: string }

const KINDS = Object.values(configKinds) as Kind[];
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const scopeOf = (k: Kind) => caseKind({ command: "set_config", radius: k.radius });
type Saved = { body: unknown; source: "bb.config" | "defaults"; saved_version: number | null; updated_by: string | null; updated_at: string | null };

/** The SAVED document for a kind (25 Oct 2026). Every form used to open at the code defaults, so saving Installer
 *  standard rates from here would have replaced the 13 live rates with an empty list. Nothing saves until this loads. */
function useSaved(kind: string) {
  return useQuery({queryKey: ["config-saved", kind], enabled: !env.mock, staleTime: 0, queryFn: async (): Promise<Saved> => {
    const r = await fetchImpl(`${env.apiBase}/api/config/${encodeURIComponent(kind)}`, {headers: await authHeaders()});
    if (!r.ok) throw Error(`HTTP ${r.status}`);
    return r.json() as Promise<Saved>;
  }});
}

export function Settings({libraryOnly=false}:{libraryOnly?:boolean}) {
  const {t}=useT();
  const meta = useConfigMeta();
  const { send, outcomes, busy } = useProposer();
  const [selected, setSelected] = useState(()=>libraryOnly?'artemis_services':new URLSearchParams(window.location.search).get('kind')??KINDS[0]!.kind);
  const [bodies, setBodies] = useState<Record<string, unknown>>(() => Object.fromEntries(KINDS.map((k) => [k.kind, clone(k.defaults)])));
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const availableKinds=libraryOnly?KINDS.filter(k=>k.kind==='artemis_services'):KINDS;
  const kind = availableKinds.find((k) => k.kind === selected) ?? availableKinds[0]!;
  const saved = useSaved(kind.kind);
  const [bases, setBases] = useState<Record<string, unknown>>({});
  // The saved document becomes the form the first time it arrives. The form is not shown before then, so nothing can be
  // edited on top of the defaults.
  useEffect(() => {
    if (!saved.data || kind.kind in bases) return;
    const loaded = clone(kind.kind==='artemis_services'?ArtemisServices.parse(saved.data.body):saved.data.body);
    setBases((b) => ({...b, [kind.kind]: loaded}));
    setBodies((b) => ({...b, [kind.kind]: clone(loaded)}));
  }, [saved.data, kind.kind, bases]);
  const base = env.mock ? kind.defaults : bases[kind.kind];
  const loaded = env.mock || base !== undefined;
  const body = bodies[kind.kind];
  const reason = reasons[kind.kind] ?? "";
  const dirty = loaded && JSON.stringify(body) !== JSON.stringify(base);
  const issues = useMemo<Issue[]>(() => {
    const r = kind.schema.safeParse(body);
    return r.success ? [] : (r.error.issues as Issue[]);
  }, [kind, body]);
  const mock = (meta.data ?? []).find((m) => m.kind === kind.kind);
  const km = env.mock ? mock : saved.data?.source === "bb.config" && saved.data.saved_version !== null ? {version: saved.data.saved_version, updated_by: saved.data.updated_by ?? "—", updated_at: saved.data.updated_at ?? ""} : undefined;
  const outcome = outcomes[kind.kind];
  const canSave = loaded && issues.length === 0 && reason.trim().length >= 3 && !busy[kind.kind];

  const setBody = (v: unknown) => setBodies((b) => ({ ...b, [kind.kind]: v }));

  async function save() {
    const r = kind.schema.safeParse(body);
    if (!r.success) return;
    await send(kind.kind, "set_config", { kind: kind.kind, version: kind.version, body: r.data, reason: reason.trim() }, `Saved · version ${(km?.version ?? 0) + 1} · previous versions kept`);
    setBases((b) => ({...b, [kind.kind]: clone(r.data)}));
    void saved.refetch();
  }

  const title=libraryOnly?t('service.libraryTitle'):kind.title;
  return (
    <Page kicker={libraryOnly?t('service.libraryKicker'):"System"} title={libraryOnly?title:"Settings"} lede={libraryOnly?t('service.libraryLede'):"Every page here is generated from a configurator schema. Change the config, not the code."} actions={<span className="text-micro text-muted-foreground">{km ? libraryOnly?t('service.libraryVersion',{title,version:km.version,by:km.updated_by,date:day(km.updated_at)}):`${kind.title} · version ${km.version} · edited by ${km.updated_by} ${day(km.updated_at)}` : libraryOnly?t('service.libraryDefaults',{title}):`${kind.title} · on defaults`}</span>}>
      {!libraryOnly&&<AgentAccountCard />}
      <div className={libraryOnly?"":"grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,3fr)]"}>
        {!libraryOnly&&<nav aria-label="Configurators" className="flex gap-1 overflow-x-auto xl:block xl:space-y-1 xl:overflow-visible">
          {availableKinds.map((k) => {
            const on = k.kind === kind.kind;
            const edited = JSON.stringify(bodies[k.kind]) !== JSON.stringify(env.mock ? k.defaults : bases[k.kind] ?? bodies[k.kind]);
            return (
              <button key={k.kind} type="button" onClick={() => setSelected(k.kind)} aria-current={on ? "page" : undefined}
                className={cn("w-full min-w-[12rem] shrink-0 rounded-md border px-3 py-2 text-left xl:min-w-0", on ? "border-primary/40 bg-accent text-accent-foreground" : "bg-card hover:bg-muted")}>
                <div className="flex items-center gap-2 text-sm font-medium">{k.title}{edited && <span className="h-1.5 w-1.5 rounded-full bg-warning" title="unsaved changes" />}</div>
                <div className="line-clamp-2 text-micro text-muted-foreground">{k.description}</div>
              </button>
            );
          })}
        </nav>}

        <section className="rounded-lg border bg-card">
          <header className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
            <h2 className="font-display text-sm font-semibold">{title}</h2>
            <KindChip kind={scopeOf(kind)} />
            <span className="text-micro text-muted-foreground">{libraryOnly?t('service.libraryDescription'):kind.description}</span>
          </header>

          {!loaded && <p role="status" className="mx-4 mt-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-micro text-warning">{saved.isError ? libraryOnly?t('service.libraryLoadFailed'):"Could not load the saved version, so saving is off. Reload the page." : libraryOnly?t('service.libraryLoading'):"Loading the saved version…"}</p>}
          {loaded && kind.kind === "installer_standard_rates" && <MissingRates value={body} onChange={setBody} />}
          {loaded && <div className="p-4">
            {kind.kind==='artemis_services'?<><StandardServiceSettings value={body} onChange={setBody}/><details className="mt-4"><summary className="cursor-pointer text-sm">{t('service.advanced')}</summary><SettingsForm key={kind.kind} schema={kind.schema as unknown as Schema} value={body} onChange={setBody}/></details></>:<SettingsForm key={kind.kind} schema={kind.schema as unknown as Schema} value={body} onChange={setBody} />}
          </div>}

          {issues.length > 0 && (
            <ul className="mx-4 mb-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-micro text-warning">
              {issues.slice(0, 6).map((i, n) => <li key={n}><span className="font-mono">{i.path.map(String).join(".") || "document"}</span> · {i.message}</li>)}
              {issues.length > 6 && <li>… and {issues.length - 6} more</li>}
            </ul>
          )}

          <footer className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
            <Input aria-label={libraryOnly?t('service.changeReason'):"What changed"} placeholder={libraryOnly?t('service.changeReasonPlaceholder'):"What changed, in a sentence (kept with the version)"} value={reason} onChange={(e) => setReasons((r) => ({ ...r, [kind.kind]: e.target.value }))} className="max-w-md" maxLength={500} />
            <Button size="sm" disabled={!canSave} onClick={() => void save()}>{km ? libraryOnly?t('service.saveVersion',{version:km.version+1}):`Save as version ${km.version + 1}` : libraryOnly?t('service.save'):"Save"}</Button>
            <Button size="sm" variant="ghost" disabled={!dirty} onClick={() => setBody(clone(base))}>{libraryOnly?t('service.resetSaved'):"Reset to saved"}</Button>
            {outcome && <span className={cn("text-micro", OUTCOME_TONE[outcome.tone])}>{outcome.text}</span>}
            <span className="ml-auto text-micro text-muted-foreground">{libraryOnly?t('service.saveNote',{scope:scopeOf(kind)}):"Checked against the schema on save · previous versions kept · scope: "+scopeOf(kind)}</span>
          </footer>
        </section>
      </div>
    </Page>
  );
}
